<!-- Friends-page presentation only. Presence and admission use the shared helpers. -->
<script lang="ts">
  import { tick } from 'svelte';
  import AvatarMark from './AvatarMark.svelte';
  import Button from '$lib/ui/Button.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import { titleHref } from '$lib/ui/nav.svelte';
  import { personName } from './people';
  import { joinableRoom, presenceLine } from './presence-line';
  import type { FriendPresence, PublicProfile } from './protocol';

  let { friend, presence, busy = false, onjoin, onremove }: {
    friend: PublicProfile;
    presence?: FriendPresence | undefined;
    busy?: boolean;
    onjoin: (friend: PublicProfile) => void;
    onremove: (friend: PublicProfile) => void;
  } = $props();
  let confirming = $state(false);
  let moreButton = $state<HTMLButtonElement>();
  let keepButton = $state<HTMLButtonElement>();
  const line = $derived(presenceLine(presence));
  const roomId = $derived(joinableRoom(presence));
  const href = $derived(line.kind === 'watching' && line.imdbId
    ? titleHref(line.mediaKind === 'series' ? 'series' : 'movie', line.imdbId) : null);

  async function confirmRemoval() {
    confirming = true;
    await tick();
    keepButton?.focus();
  }
  async function keepFriend() {
    confirming = false;
    await tick();
    moreButton?.focus();
  }
</script>

<li class="person" class:watching={line.kind === 'watching'}>
  <div class="identity">
    <AvatarMark avatar={friend.avatar} color={friend.color} size={64} square />
    <div class="name">
      <h3>{personName(friend)}</h3>
      <span class="handle">@{friend.handle}</span>
    </div>
    <button class="more" type="button" bind:this={moreButton} aria-label="Manage friendship with {personName(friend)}" aria-expanded={confirming} disabled={busy} onclick={confirmRemoval}>
      <Icon name="dots" size={20} />
    </button>
  </div>
  <div class="activity">
    {#if line.kind === 'watching'}
      <span class="status"><span class="dot" aria-hidden="true"></span>Watching now</span>
      {#if href}<a class="title" {href}>{line.title}</a>{:else}<p class="title">{line.title}</p>{/if}
    {:else if line.kind === 'in-room'}
      <span class="status"><span class="dot" aria-hidden="true"></span>Player open</span>
      <p class="activity-note">No title selected yet</p>
    {:else if line.kind === 'online'}
      <span class="status"><span class="dot" aria-hidden="true"></span>Online</span>
      <p class="activity-note">Not watching a title right now</p>
    {:else}
      <!-- A private account is intentionally indistinguishable from offline. -->
      <span class="status quiet">Activity not shared</span>
      <p class="activity-note">You can still invite them from the player.</p>
    {/if}
  </div>
  {#if confirming}
    <div class="confirmation">
      <p>Remove {personName(friend)} from your friends?</p>
      <div class="actions">
        <button class="keep" type="button" bind:this={keepButton} disabled={busy} onclick={keepFriend}>Keep friend</button>
        <Button loading={busy} onclick={() => onremove(friend)}>Remove</Button>
      </div>
    </div>
  {:else if roomId}
    <div class="join"><Button variant="primary" icon="play" disabled={busy} onclick={() => onjoin(friend)}>Join them</Button></div>
  {/if}
</li>

<style>
  .person { min-width: 0; padding: var(--space-5); border: 1px solid var(--line); border-radius: var(--radius-lg); background: var(--surface-1); }
  .person.watching { border-color: color-mix(in oklab, var(--live) 50%, var(--line)); }
  .identity { display: flex; align-items: center; gap: var(--space-3); }
  .name { flex: 1; min-width: 0; }
  h3 { font-size: var(--text-lg); font-weight: var(--weight-semi); line-height: var(--leading-snug); overflow-wrap: anywhere; }
  .handle { display: block; margin-top: var(--space-1); color: var(--text-dim); font-size: var(--text-sm); overflow-wrap: anywhere; }
  .more { display: grid; place-items: center; width: 44px; height: 44px; flex: none; border-radius: var(--radius-pill); color: var(--text-dim); align-self: flex-start; }
  .more:hover { color: var(--text); background: var(--surface-2); }
  .activity { margin-top: var(--space-5); }
  .status { display: flex; gap: var(--space-2); align-items: center; font-size: var(--text-sm); color: var(--text); }
  .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--live); }
  .quiet, .activity-note { color: var(--text-dim); }
  .activity-note { margin-top: var(--space-1); font-size: var(--text-sm); }
  .title { display: block; color: var(--text); font-size: var(--text-lg); font-weight: var(--weight-semi); overflow-wrap: anywhere; line-height: var(--leading-snug); margin-top: var(--space-2); }
  a.title { padding-block: var(--space-2); margin-block: 0 calc(-1 * var(--space-2)); text-decoration-thickness: 1px; text-underline-offset: 4px; }
  a.title:hover { text-decoration: underline; }
  .join, .confirmation { margin-top: var(--space-4); }
  .confirmation { border-top: 1px solid var(--line); padding-top: var(--space-4); }
  .confirmation p { font-size: var(--text-sm); overflow-wrap: anywhere; }
  .actions { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
  .keep { min-height: 44px; padding-inline: var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text); }
  .keep:hover { background: var(--surface-3); }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button:focus-visible, a:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 4px; }
  @media (max-width: 480px) { .person { padding: var(--space-4); } }
</style>
